import React, { memo, useCallback, useEffect, useState } from 'react';
import './index.css';
import {Button, Input} from "antd";
import IntlMessages from "../../../../util/IntlMessages";
import DeviceRadioButtonChecked from "../../../../assets/svg/DeviceRadioButtonChecked";
import DeviceRadioButtonUnchecked from "../../../../assets/svg/DeviceRadioButtonUnchecked";
import DeviceLock from "../../../../assets/svg/DeviceLock";
import DeviceMessageSend from "../../../../assets/svg/DeviceMessageSend";
import {
  formatLocation,
  formatTimestampToCustomString,
  getFormattedClientName,
  getFormattedDeviceName
} from "../../../../util/common";
import {useIntl} from "react-intl";
import AtomicButton from "../../../../components/AtomicButton";

const { TextArea } = Input;

// Button/Text/SVG 대체 예시 (원래 컴포넌트 있으면 교체)
const StyledDefaultButton = ({ label, onClick, style, fontColor }) => (
  <button
    className="styled-default-button"
    style={{
      ...style,
      color: fontColor || undefined,
    }}
    onClick={onClick}
  >
    {label}
  </button>
);
// 유틸함수(더미) - 실제 유틸로 교체 필요
// const formatLocation = deviceInfo => deviceInfo.location || 'Unknown';
// const formatTimestampToCustomString = ts => ts ? new Date(ts).toLocaleString() : '';
// const getFormattedClientName = async () => 'WebClient';
// const getFormattedDeviceName = async () => 'ChromePC';

// SelectionItem 컴포넌트 (메모이제이션)
const SelectionItem = memo(({ title, message, active, onClick }) => (
  <div
    className={`selection-item${active ? ' active' : ''}`}
    onClick={onClick}
    tabIndex={0}
    role="radio"
    aria-checked={active}
  >
    <div className="selection-content">
      <div className="row-center">
        {active ? <DeviceRadioButtonChecked /> : <DeviceRadioButtonUnchecked />}
        <span className="selection-title">{title}</span>
      </div>
      <div className="row-center">
        <span className="selection-message">{message}</span>
      </div>
    </div>
  </div>
));

function RegisterDevice({
                          onCancel,
                          onOk,
                          onClose,
                          deviceInfo = {},
                          checkApproval,
                          clearCheckApproval,
                          t = key => key, // 다국어 함수 대체(실제는 i18n)
                        }) {
  const intl = useIntl();

  const [selectedOption, setSelectedOption] = useState('trust');
  const [isRestrictionMode, setIsRestrictionMode] = useState(false);
  const [registerDeviceStatus, setRegisterDeviceStatus] = useState(undefined);
  const [deviceClientInfo, setDeviceClientInfo] = useState({
    deviceName: intl.formatMessage({ id: 'vlink.login.device.unknown'}),
    clientName: intl.formatMessage({ id: 'vlink.login.device.unknown'}),
  });
  const [requestMessageToHost, setRequestMessageToHost] = useState('');
  const maxLength = 100;

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  const handleConfirm = useCallback(() => {
    const values = {
      username: deviceInfo.username || deviceInfo.access_mode?.pending?.user?.username,
      ip: deviceInfo.access_mode?.client_ip ||
        deviceInfo.access_mode?.pending?.ip,
      device_uuid: deviceInfo.access_mode?.device_uuid ||
        deviceInfo.access_mode?.pending?.device_uuid,
      os: deviceClientInfo.deviceName,
      client: deviceClientInfo.clientName,
      need_to_limit: false,
      ql_uuid: deviceInfo.ql_uuid,
      message: requestMessageToHost,
    };
    // console.log('🟦', requestMessageToHost);
    if (isRestrictionMode) {
      if (selectedOption === 'temporary') {
        onCancel?.();
      } else {
        values.is_requested = true;
        onOk?.(values);
      }
    } else {
      values.need_to_limit = selectedOption === 'temporary';
      onOk?.(values);
    }
  }, [
    isRestrictionMode,
    deviceInfo,
    deviceClientInfo,
    selectedOption,
    onOk,
    onCancel,
    requestMessageToHost,
  ]);

  const handleTrustOption = useCallback(() => setSelectedOption('trust'), []);
  const handleTemporaryOption = useCallback(() => setSelectedOption('temporary'), []);

  const { connected_at = new Date().toLocaleString() } = deviceInfo;
  const connectionTime = formatTimestampToCustomString(connected_at);
  const requestTime = formatTimestampToCustomString(
    deviceInfo.access_mode?.pending?.created_at || connected_at,
  );
  const location = formatLocation(deviceInfo) || intl.formatMessage({ id: 'vlink.login.location.unknown'});

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [deviceName, clientName] = await Promise.all([
        getFormattedDeviceName(),
        getFormattedClientName(),
      ]);
      if (mounted) setDeviceClientInfo({ deviceName, clientName });
    })();

    setIsRestrictionMode(
      deviceInfo.access_mode?.access_mode_settings?.is_open === false ||
      deviceInfo.access_mode?.pending !== undefined,
    );
    setRegisterDeviceStatus(deviceInfo.access_mode?.pending?.status);

    if (deviceInfo.access_mode?.pending) {
      checkApproval?.({
        username: deviceInfo.access_mode.pending.user?.username,
        ip: deviceInfo.access_mode.pending.ip,
        device_uuid: deviceInfo.access_mode.pending.device_uuid,
      });
    }
    return () => {
      mounted = false;
      clearCheckApproval?.();
    };
  }, []); // deviceInfo를 의존성에서 제거 (필요시 추가)

  return (
    <div className="register-device-container">
      <div className="scroll-view">
        {isRestrictionMode && (
          <div style={{ paddingBottom: 10 }}>
            <div className="info-row">
              <DeviceLock />
              <span className="selection-title">
                <IntlMessages id="vlink.login.device.enableRestrictedMode.title" />
              </span>
            </div>
            <div>
              <span className="warning-message">
                <IntlMessages id="vlink.login.device.enableRestrictedMode.message" />
              </span>
            </div>
          </div>
        )}
        <div className="content">
          {/* 기기 정보 */}
          <div className="info-section">
            <div className="info-row">
              <span className="bullet-icon" />
              <span className="label">
                <IntlMessages id="vlink.login.device.name" />:
              </span>
              <span className="info-value">
                {deviceClientInfo.deviceName}
              </span>
            </div>
            {/*<div className="info-row">*/}
            {/*  <span className="bullet-icon" />*/}
            {/*  <span className="label">*/}
            {/*    <IntlMessages id="vlink.login.device.location" />:*/}
            {/*  </span>*/}
            {/*  <span className="info-value">{location}</span>*/}
            {/*</div>*/}
            <div className="info-row">
              <span className="bullet-icon" />
              <span className="label">
                <IntlMessages id="vlink.login.device.connectionTime" />:
              </span>
              <span className="info-value">{connectionTime}</span>
            </div>
          </div>

          {/* 헤더/상태/옵션 선택 */}
          {registerDeviceStatus === 'need_to_approve' ? (
            <div className="header" style={{ height: 190 }}>
              <div
                style={{
                  padding: 15,
                  borderRadius: 30,
                  marginBottom: 16,
                  display: 'flex',
                  background: '#EFEFEF',
                }}
              >
                <DeviceMessageSend />
              </div>
              <span className="header-title">
                <IntlMessages id="vlink.login.device.waitingApprovalTitle" />
              </span>
              <span className="header-subtitle">
                <IntlMessages id="vlink.login.device.waitingApprovalSubtitle" />
              </span>
            </div>
          ) : isRestrictionMode ? (
            <div className="header">
              <span className="header-title">
                <IntlMessages id="vlink.login.device.registerTitleInRestrictMode" />
              </span>
              <span className="header-subtitle">
                <IntlMessages id="vlink.login.device.registerSubtitle" />
              </span>
            </div>
          ) : (
            <div className="header">
              <span className="header-title">
                <IntlMessages id="vlink.login.device.registerTitle" />
              </span>
              <span className="header-subtitle">
                <IntlMessages id="vlink.login.device.registerSubtitle" />
              </span>
            </div>
          )}

          {/* 요청 대기/옵션 */}
          {registerDeviceStatus === 'need_to_approve' ? (
            <div className="info-section" style={{ background: '#EBF5FF' }}>
              <div className="info-row">
                <span className="bullet-icon" />
                <span className="label">
                  <IntlMessages id="vlink.login.device.requestTime" />
                </span>
                <span className="info-value">{requestTime}</span>
              </div>
              <div className="info-row">
                <span className="bullet-icon" />
                <span className="label">
                  <IntlMessages id="vlink.login.device.status" />
                </span>
                <span className="info-value">
                  <IntlMessages id={`vlink.login.device.approvalStatus.${registerDeviceStatus}`} />
                </span>
              </div>
            </div>
          ) : (
            <div className="selection-section">
              <SelectionItem
                title={
                  isRestrictionMode
                    ? <IntlMessages id="vlink.login.device.trustOptionInRestrictionMode.title" />
                    : <IntlMessages id="vlink.login.device.trustOption.title" />
                }
                message={
                  isRestrictionMode
                    ? <IntlMessages id='vlink.login.device.trustOptionInRestrictionMode.message' />
                    : <IntlMessages id='vlink.login.device.trustOption.message' />
                }
                active={selectedOption === 'trust'}
                onClick={handleTrustOption}
              />
              <SelectionItem
                title={
                  isRestrictionMode
                    ? <IntlMessages id='vlink.login.device.temporaryOptionInRestrictionMode.title' />
                    : <IntlMessages id='vlink.login.device.temporaryOption.title' />
                }
                message={
                  isRestrictionMode
                    ? <IntlMessages id='vlink.login.device.temporaryOptionInRestrictionMode.message' />
                    : <IntlMessages id='vlink.login.device.temporaryOption.message' />
                }
                active={selectedOption === 'temporary'}
                onClick={handleTemporaryOption}
              />
              {isRestrictionMode &&
              <div>
                {/* Label */}
                <div style={{
                  fontWeight: 400,
                  fontSize: 14,
                  marginBottom: 8,
                  color: '#181818',
                }}>
                  <IntlMessages id="vlink.login.device.requestMessageOptional" />
                </div>

                {/* TextArea */}
                <TextArea
                  value={requestMessageToHost}
                  onChange={e => setRequestMessageToHost(e.target.value)}
                  maxLength={maxLength}
                  placeholder={intl.formatMessage({ id: 'vlink.login.device.messageToHost' })}
                  style={{
                    width: '100%',
                    fontSize: 14,
                    color: '#444',
                    borderRadius: 4,
                    // border: '1px solid #B9B9B9',
                    padding: '8px 20px',
                    // boxSizing: 'border-box',
                    background: '#fff',
                    marginBottom: 8,
                    resize: 'none',
                  }}
                  rows={2}
                  disabled={isRestrictionMode && selectedOption !== 'trust'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                />

                {/* Char count */}
                <div style={{
                  color: '#888',
                  fontSize: 12,
                  marginTop: 2,
                  marginLeft: 2,
                  fontFamily: 'inherit',
                }}>
                  {requestMessageToHost.length}/{maxLength} {intl.formatMessage({ id: 'vlink.login.device.charCount' })}
                </div>
              </div>
              }
            </div>
          )}
        </div>
      </div>
      {/* 하단 버튼 */}
      {registerDeviceStatus === 'need_to_approve' ? (
        <div className="footer">
          <AtomicButton
            label={intl.formatMessage({ id: 'vlink.login.device.button.requestAccessAgain'})}
            onClick={() => setRegisterDeviceStatus(undefined)}
          />
          <AtomicButton
            type={"default"}
            label={intl.formatMessage({ id: 'vlink.common.confirm'})}
            onClick={handleClose}
          />
        </div>
      ) : (
        <div className="footer">
          <AtomicButton
            type={"default"}
            label={intl.formatMessage({ id: 'vlink.common.cancel'})}
            onClick={handleCancel}
          />
          <AtomicButton
            label={
              isRestrictionMode
                ? selectedOption === 'trust'
                  ? intl.formatMessage({ id: 'vlink.login.device.button.requestAccess'})
                  : intl.formatMessage({ id: 'vlink.login.device.button.notNow'})
                : intl.formatMessage({ id: 'vlink.common.confirm'})
            }
            onClick={handleConfirm}
          />
        </div>
      )}
    </div>
  );
}

export default RegisterDevice;
